import { computed, toRaw, toValue, watch, watchEffect } from "vue";
import type { InputConfig } from "./useForm"
import useWooCart from "./useWooCart";
import useForm from "./useForm";
import useItlpApi from "./useItlpApi";
import type { WoocommerceBillingAddress, WoocommerceShippingAddress } from "@/types/woocommerce.types";
import useGlobalAlert from "./useGlobalAlert";

const billingFields = [
	{
		key: 'firstname',
		checkout_field: 'first_name',
	},
	{
		key: 'lastname',
		checkout_field: 'last_name'
	},
	{
		key: 'address',
		checkout_field: 'address_1'
	},
	{
		key: 'city'
	},
	{
		key: 'province',
		checkout_field: 'state'
	},
	{
		key: 'postcode'
	},
	{
		key: 'country',
		default: 'IT'
	},
	{
		key: 'phone'
	}
];


export default function (){
	const {checkoutFields, updateCustomer, updateCustomerBodyError, cart} = useWooCart();
	const {countries, countryLocale, countryStates, shippingCountries} = useItlpApi();
	const localeDefault = countryLocale.value['default'] || {};
	const {addAlert} = useGlobalAlert();

	function localizeProvince(country: string|null|undefined, currentConfig: InputConfig): Partial<InputConfig>{
		const locale = country ? Object.assign({}, localeDefault, countryLocale.value[country] || {}) : localeDefault;
		const newConfig: Partial<InputConfig> = {
			label: locale?.state?.label || currentConfig.label,
			type: locale?.state?.hidden ? 'hidden' : 'select',
			required: typeof(locale?.state?.required) == 'boolean' ? locale?.state?.required : true,
			options: country ? countryStates.value[country] : {}
		};

		if(Array.isArray(newConfig.options) && newConfig.options.length == 0) newConfig.options = {};

		if(newConfig.type == 'select' && (typeof(newConfig.options) != 'object' || !Object.keys(newConfig.options).length)){
			newConfig.type = 'text';
		}

		return newConfig;
	}

	function localizePostcode(country: string|null|undefined, currentConfig: InputConfig): Partial<InputConfig>{
		const locale = country ? Object.assign({}, localeDefault, countryLocale.value[country] || {}) : localeDefault;
		return {
			label: locale?.postcode?.label || currentConfig.label,
			type: locale?.postcode?.hidden ? 'hidden' : 'text',
			required: typeof(locale?.postcode?.required) == 'boolean' ? locale?.postcode?.required : true
		};
	}

	const shippingInputOptions = computed(() => {
		const defaultCountry = ((cart.value?.shipping_address['country']) || (cart.value?.billing_address['country']) || checkoutFields.value?.shipping['shipping_country'].default || 'IT') as string|null|undefined;

		return billingFields.reduce((carry, field) => {
			const billingFieldKey = 'shipping_'+(field.checkout_field || field.key);
			if(!checkoutFields.value?.shipping[billingFieldKey]) return carry;

			const initValue = ((cart.value?.shipping_address[(field.checkout_field || field.key) as keyof WoocommerceShippingAddress]) || (cart.value?.billing_address[(field.checkout_field || field.key) as keyof WoocommerceBillingAddress])) as string|null|undefined;

			const inputOptions: InputConfig = {
				key: field.key,
				apiFormKey: field.checkout_field,
				label: checkoutFields.value?.shipping[billingFieldKey].label,
				type: checkoutFields.value?.shipping[billingFieldKey].type,
				options: checkoutFields.value?.shipping[billingFieldKey].options,
				placeholder: checkoutFields.value?.shipping[billingFieldKey].placeholder,
				required: checkoutFields.value?.shipping[billingFieldKey].required,
				default: initValue || checkoutFields.value?.shipping[billingFieldKey].default || field.default,
			};

			if(inputOptions.key == 'country'){
				inputOptions.type = 'select';
				inputOptions.options = toValue(shippingCountries);
			}

			const locale = defaultCountry ? Object.assign({}, localeDefault, countryLocale.value[defaultCountry] || {}) : localeDefault;

			if(inputOptions.key == 'province'){
				Object.assign(inputOptions, localizeProvince(defaultCountry, inputOptions))
			}

			if(locale?.postcode && inputOptions.key == 'postcode'){
				Object.assign(inputOptions, localizePostcode(defaultCountry, inputOptions))
			}

			carry.push(inputOptions);

			return carry;
		}, [] as InputConfig[]);
	});

	const form = useForm();
	const {build: buildFrom} = form;

	watch(() => toValue(shippingInputOptions), (inputOptions) => {
		buildFrom({
			inputs: inputOptions
		});
	}, {immediate: true});

	const selectedCountry = computed(() => {
		return form.controllers.value['country'] && form.controllers.value['country'].value;
	});

	watch(toValue(selectedCountry), (selectedCountryValue) => {
		const _selectedCountry = toValue(selectedCountryValue) as string;
		const provinceConfig = form.controllers.value['province'].getConfig();
		const postcodeConfig = form.controllers.value['postcode'].getConfig();
		form.controllers.value['province'].value.value = null;
		form.controllers.value['province'].setConfig(Object.assign({}, provinceConfig, localizeProvince(_selectedCountry, provinceConfig)));
		form.controllers.value['postcode'].setConfig(Object.assign({}, postcodeConfig, localizePostcode(_selectedCountry, postcodeConfig)));
	});

	watch(() => toValue(updateCustomerBodyError), () => {
		const formErrors: Record<string, string> = {};
		if(updateCustomerBodyError.value && typeof(updateCustomerBodyError.value) == 'object'){
			const erroCode = updateCustomerBodyError.value.code;
			const shippingAddressErrorCode = updateCustomerBodyError.value.data?.details?.shipping_address?.code;
			if(erroCode == 'rest_invalid_param' && shippingAddressErrorCode == 'invalid_state'){
				formErrors['province'] = updateCustomerBodyError.value.data?.details?.shipping_address?.message;
			}else if(erroCode == 'rest_invalid_param' && shippingAddressErrorCode == 'invalid_postcode'){
				formErrors['postcode'] = updateCustomerBodyError.value.data?.details?.shipping_address?.message;
			}
		}

		form.errors.value = formErrors;

		if(updateCustomerBodyError.value?.data?.params?.shipping_address) addAlert(updateCustomerBodyError.value?.data?.params?.shipping_address, 5000);
	});

	function save(){
		return updateCustomer({
			shipping_address: form.getApiFormData()
		});
	}

	return {
		shippingInputOptions,
		form,
		save
	};
}